import ExpandableSection from '@risk-smart/themed-cloudscape-components/expandable-section';
import SpaceBetween from '@risk-smart/themed-cloudscape-components/space-between';
import Button from '@risksmart-app/components/src/button';
import Table from '@risksmart-app/components/src/table';
import type { GetControlByIdQuery } from '@risksmart-app/web-graphql-client/generated/graphql';
import type { FC } from 'react';
import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useI18NSummaryHelpContent } from 'src/components/help-panel/useSummaryHelpContent';
import { useGetOversightAssessmentTestResultsByControlId } from 'src/hooks/queries/test-result/useGetOversightAssessmentTestResultsByControlId';
import { useIsFeatureFlagEnabled } from 'src/hooks/useIsFeatureFlagEnabled';
import { Permission } from 'src/rbac/Permission';

import { getCounter } from '@/utils/collectionUtils';

import TestResultModal from '../TestResultModal';
import { useGetCollectionTableProps } from './oversightPerformanceRatingConfig';

type Props = {
  control: GetControlByIdQuery['control'][number];
};

const OversightPerformanceRatingRegister: FC<Props> = ({ control }) => {
  useI18NSummaryHelpContent('testResults.tabHelp');
  const { t } = useTranslation(['common']);
  const { t: st } = useTranslation(['common'], {
    keyPrefix: 'testResults',
  });
  const oversightRatingEntryEnabled = useIsFeatureFlagEnabled(
    'oversight_rating_performance_entry'
  );
  const [isEditOpen, setIsEditOpen] = useState(false);
  const [openTestResultId, setOpenTestResultId] = useState<
    string | undefined
  >();

  const { data, loading, refetch } =
    useGetOversightAssessmentTestResultsByControlId({
      queryArgs: { controlId: control.Id },
    });

  const handleTestResultModalClose = async (saved?: boolean) => {
    if (saved) {
      await refetch();
    }
    setOpenTestResultId(undefined);
    setIsEditOpen(false);
  };

  const onAddRating = () => {
    setOpenTestResultId(undefined);
    setIsEditOpen(true);
  };

  const tableProps = useGetCollectionTableProps((testResult) => {
    setOpenTestResultId(testResult.Id);
    setIsEditOpen(true);
  }, data?.control_test_oversight_result);

  return (
    <>
      <SpaceBetween size={'m'}>
        {oversightRatingEntryEnabled && (
          <div className={'flex justify-end'}>
            <Permission
              permission={'insert:control_test_oversight_result'}
              parentObject={control}
            >
              <Button
                iconName={'add-plus'}
                variant={'primary'}
                formAction={'none'}
                onClick={onAddRating}
              >
                {t('assessments.add_rating_button')}
              </Button>
            </Permission>
          </div>
        )}
        <ExpandableSection
          header={
            <div className={'flex space-x-2'}>
              <span>{st('oversightRatingSubheading')}</span>
              <span className={'text-grey font-normal'}>
                {getCounter(tableProps.totalItemsCount ?? 0, loading)}
              </span>
            </div>
          }
          defaultExpanded={true}
        >
          <Table
            {...tableProps}
            resizableColumns={true}
            variant={'embedded'}
            loading={loading}
            loadingText={t('loadingTestResults') ?? ''}
            sortingDisabled={false}
          />
        </ExpandableSection>
      </SpaceBetween>
      {isEditOpen && control.Id && (
        <TestResultModal
          parentControlId={control.Id}
          Id={openTestResultId}
          onDismiss={handleTestResultModalClose}
          assessmentMode={'oversight_assessment'}
        />
      )}
    </>
  );
};

export default OversightPerformanceRatingRegister;
